import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'

import { ApiMetaType } from '../clients/apiRequest'
import { observationClient } from '../clients/ObservationClient'
import { GateFeedFilter, ObservationSort } from '../clients/type'
import { userClient } from '../clients/UserClient'
import { ReportType } from '../models/client/constants'
import {
  GateObservation,
  ObservationDraft,
  ObservationMetadata,
  GateFeedColumnKey,
  SortingAction,
} from '../models/client/observation'
import { selectSiteIds } from '../store/sites'
import { selectUserToken, selectUserUserId, selectUser } from '../store/user'
import { ApiError } from '../util/errors/ApiError'
import { BaseError } from '../util/errors/BaseError'

const DEFAULT_COLUMNS: Array<GateFeedColumnKey> = [
  'assetPreviewUrl',
  'assetDirection',
  'DATE',
  'TIME',
  'vehicleCarrierName',
  'loadNumber',
  'chassisNumber',
  'vehicleNumber',
]

type Return = {
  gateObservations: GateObservation[]
  filters: GateFeedFilter[]
  isLoading: boolean
  isDeleting: boolean
  isAdding: boolean
  isEditing: boolean
  hasError: boolean
  page: number
  pageSize: number
  isColumnSettingsLoading: boolean
  totalPages: number
  totalItems: number
  itemIndex: number
  sort: ObservationSort | null
  userColumnKeys: Array<GateFeedColumnKey>
  isSingleObservationLoading: boolean
  singleObservationError: Error | null
  setPage: (pageIndex: number) => void
  addFilter: (filter: GateFeedFilter) => void
  setSort: SortingAction
  removeFilter: (filter: GateFeedFilter) => void
  clearAllFilters: () => void
  addObservation: (
    draft: ObservationDraft,
    meta?: ObservationMetadata,
    options?: any
  ) => Promise<void>
  editObservation: (
    observationId: string,
    draft: ObservationDraft,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  deleteObservation: (
    observationId: string,
    draft: ObservationDraft,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  outgateObservation: (
    observationId: string,
    observation: GateObservation,
    outgateDateTime: string,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  setReportUserColumns: (nextColumnKeys: Array<GateFeedColumnKey>) => Promise<void>
  getObservationById: (
    observationId: string,
    requestParams?: { token?: string }
  ) => Promise<GateObservation>
  refresh: () => any
}

type Params = {
  reportType: ReportType
  queryPollMS?: number
  inventoryOnly?: boolean
  includeVehicles?: boolean
  defaultColumns?: Array<GateFeedColumnKey>
  onEditSuccess?: (nextObservation: GateObservation) => void
  onEditError?: (error: Error) => void
  onAddError?: (error: Error) => void
  onAddSuccess?: (nextObservation: GateObservation) => void
}

// const translateSiteIdsToFilter = (siteIds: string[]): SiteFilter[] =>
//   siteIds.map((id) => ({ type: 'site', value: id }))

const PAGE_SIZE = 50

export const useGateObservations = (params: Params): Return => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filters, setFilters] = useState<GateFeedFilter[]>([])
  const [sort, setSort] = useState<ObservationSort | null>(null)
  const siteIds = useSelector(selectSiteIds, shallowEqual)
  const token = useSelector(selectUserToken, shallowEqual)
  const userId = useSelector(selectUserUserId, shallowEqual)
  const user = useSelector(selectUser, shallowEqual)
  const { reportType, defaultColumns = DEFAULT_COLUMNS, includeVehicles } = params
  const QUERY_KEY = useMemo(
    () => ['getSiteObservations', filters, token, page, reportType, sort],
    [filters, token, page, reportType, sort]
  )

  const COLUMN_KEY = ['getcolumnpreferences', userId, token, reportType]
  const pollSettings = params?.queryPollMS
    ? {
        refetchInterval: params.queryPollMS,
      }
    : {
        refetchInterval: 300000,
      }
  const {
    data,
    status,
    refetch,
    isFetching,
    isLoading: isQLoading,
  } = useQuery(
    QUERY_KEY,
    () => {
      if (!token) throw new Error('err')
      return observationClient.getSiteGateFeed({
        filters,
        token,
        page,
        size: PAGE_SIZE,
        reportType,
        includeVehicles,
        sort,
      })
    },
    {
      enabled: !!siteIds,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      ...pollSettings,
    }
  )

  const {
    data: gatefeedMetadata,
    status: columnStatus,
    refetch: refetchColumns,
  } = useQuery(COLUMN_KEY, () => userClient.getGateFeedPreferences({ token, userId, reportType }), {
    enabled: !!token && !!userId,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  })

  const { mutateAsync: mutateColumns, status: mutateColumnStatus } = useMutation(
    userClient.setPreferencesGatefeedColumns,
    {
      onSuccess: (nextColumns) => {
        queryClient.setQueryData(COLUMN_KEY, () => ({
          columnKeys: nextColumns,
        }))
      },
    }
  )

  // const { mutateAsync: mutateAdd, status: addStatus } = useMutation(observationClient.addObservation)
  const { mutateAsync: mutateAdd, status: addStatus } = useMutation(
    observationClient.addObservationV2
  )

  // const { mutateAsync: mutateEdit, status: editStatus } = useMutation(observationClient.editObservation)
  const { mutateAsync: mutateEdit, status: editStatus } = useMutation(
    observationClient.editObservationV2
  )

  // const { mutateAsync: mutateDelete, status: deleteStatus } = useMutation(observationClient.deleteObservation)
  const { mutateAsync: mutateDelete, status: deleteStatus } = useMutation(
    observationClient.deleteObservationV2
  )

  const { mutateAsync: mutateOutgate, status: outgateStatus } = useMutation(
    observationClient.outgateObservationV2
  )

  const {
    mutateAsync: getObservationById,
    status: byIdStatus,
    error: byIdError,
  } = useMutation(observationClient.getGateObservation)

  const handleMutation = useCallback(
    (
      // nextObservation: GateObservation | null,
      // callback?: (nextObservation: GateObservation | null) => void
      nextObservation: any,
      callback?: (nextObservation: any) => void
    ): void => {
      if (!nextObservation) {
        return callback?.(null)
      }

      type ReturnType = { data: GateObservation[]; meta: ApiMetaType }

      queryClient.setQueryData<ReturnType>(QUERY_KEY, (nextResult) => {
        if (!nextResult) {
          throw new BaseError({
            cause: new Error('Something Went Wrong'),
          })
        }

        const nextData = nextResult.data?.filter(
          (item) => item?.observationId !== nextObservation?.observationId
        )

        return {
          data: [nextObservation, ...nextData],
          meta: nextResult.meta,
        }
      })

      callback?.(nextObservation)
    },
    [QUERY_KEY, queryClient]
  )

  const isColumnSettingsLoading = mutateColumnStatus === 'loading' || columnStatus === 'loading'
  const isLoading = status === 'loading' || isFetching
  const isDeleting = deleteStatus === 'loading'
  const isOutgating = outgateStatus === 'loading'
  const isAdding = addStatus === 'loading'
  const isEditing = editStatus === 'error'
  const total = data?.meta?.total || 0
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const itemIndex = page * PAGE_SIZE
  const { columnKeys } = gatefeedMetadata || {}
  console.log([status, isLoading, isFetching])
  return {
    filters,
    isColumnSettingsLoading,
    sort,
    gateObservations: data?.data || [],
    page,
    pageSize: PAGE_SIZE,
    totalPages,
    totalItems: total,
    itemIndex,
    isLoading,
    isSingleObservationLoading: byIdStatus === 'loading',
    singleObservationError: byIdError instanceof Error ? byIdError : null,
    isDeleting,
    isAdding,
    isEditing,
    hasError: status === 'error',
    userColumnKeys: columnKeys?.length ? columnKeys : defaultColumns,
    clearAllFilters() {
      setSort(null)
      setPage(0)
      setFilters([])
    },
    removeFilter(removedFilter) {
      const nextFilters: GateFeedFilter[] = []
      filters.forEach((filter) => {
        if (filter.id === removedFilter.id) return
        nextFilters.push(filter)
      })
      setSort(null)
      setPage(0)
      setFilters(nextFilters)
    },
    addFilter(nextFilter) {
      const nextFilters: GateFeedFilter[] = [nextFilter]
      filters.forEach((filter) => {
        if (filter.type !== nextFilter.type) nextFilters.push(filter)
        else if (filter.type === 'property' && nextFilter.type === 'property') {
          let same = false
          for (let i = 0; i < Math.max(filter.value.length, nextFilter.value.length); i++) {
            const currentFilterName = filter.value[i]?.name
            const nextFilterName = nextFilter.value[i]?.name
            if (currentFilterName === nextFilterName) {
              same = true
            }
            if (same) break
          }
          if (!same) {
            nextFilters.push(filter)
          }
        }
      })
      setSort(null)
      setPage(0)
      setFilters(nextFilters)
    },
    async addObservation(nextData, nextMeta, nextOptions) {
      if (!token) {
        // FIXME: may be a problem in that this error won't be returned in the hook's error
        params?.onAddError?.(new ApiError({ type: 'Unauthorized' }))
        return
      }
      try {
        const observation = await mutateAdd({
          token,
          draft: nextData,
          meta: nextMeta,
          report: nextOptions?.report,
        })

        handleMutation(observation, params?.onAddSuccess)
      } catch (e) {
        console.error(e)

        if (e instanceof Error) {
          params?.onEditError?.(e)
        }
      }
    },
    async editObservation(observationId, nextData, nextMeta, nextOptions) {
      if (!token) {
        // FIXME: may be a problem in that this error won't be returned in the hook's error
        params?.onEditError?.(new ApiError({ type: 'Unauthorized' }))
        return
      }
      try {
        const observation = await mutateEdit({
          token,
          observationId,
          draft: nextData,
          meta: nextMeta,
          report: nextOptions?.report,
        })

        handleMutation(observation, params?.onEditSuccess)
      } catch (e) {
        console.error(e)

        if (e instanceof Error) {
          params?.onEditError?.(e)
        }
      }
    },
    async deleteObservation(observationId, nextData, nextMeta, nextOptions) {
      if (!token) {
        throw new ApiError({ type: 'Unauthorized' })
      }

      if (isDeleting) {
        return
      }

      await mutateDelete({
        token,
        observationId,
        draft: nextData,
      })
      await refetch()
    },
    async outgateObservation(observationId, nextData, outgateDateTime) {
      if (!token) {
        throw new ApiError({ type: 'Unauthorized' })
      }

      if (isOutgating) {
        return
      }

      await mutateOutgate({
        token,
        observationId,
        observation: nextData,
        outgateDateTime,
      })
      await refetch()
    },
    setPage(pageIndex) {
      setPage(pageIndex)
    },
    setSort(nextSort) {
      setSort(nextSort || null)
    },
    async setReportUserColumns(nextColumnKeys): Promise<void> {
      if (!token || !userId) throw new ApiError({ type: 'Unauthorized' })

      const meta = { ...user.meta }
      meta.reports = { ...meta?.reports, [reportType]: nextColumnKeys }
      await mutateColumns({ token, userId, columnKeys: nextColumnKeys, reportType, meta })
      refetchColumns()
    },
    getObservationById(observationId, requestParams) {
      if (!requestParams?.token || !token) throw new ApiError({ type: 'Unauthorized' })
      return getObservationById({ token: requestParams?.token || token, observationId })
    },
    refresh() {
      return refetch()
    },
  }
}
